import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ChatCircle, SquaresFour, ShareNetwork, MapPin, FolderSimple, NotePencil, SignOut, ShieldWarning, CaretRight, Plus, Trash, Gear } from '@phosphor-icons/react';
import { formatRelativeTime } from './ChatMessage';
import ThemeToggle from './ThemeToggle';
import { api } from '../services/api';

/**
 * Context-aware sidebar component.
 * - Default mode: shows category navigation (Gallery/Entities pages)
 * - Chat mode: shows chat session list (when chatConfig prop is provided)
 */
export default function Sidebar({
  categories = [],
  selectedCategory,
  selectedSubcategory,
  onSelectCategory,
  chatConfig,
  collectionsConfig,
  user,
  onLogout,
  isOpen,
  onClose,
}) {
  const [expandedCategories, setExpandedCategories] = useState({});
  const location = useLocation();

  // Sign out of every device (COL-192) — a mis-click has real blast radius,
  // so confirm first (same window.confirm pattern as Review.jsx's logout
  // guard). Revoking the caller's own refresh token everywhere means this
  // browser's session is dead too, so finish with the normal logout flow
  // rather than leaving the user looking "logged in" until their access
  // token expires on its own.
  const handleSignOutEverywhere = async () => {
    if (!window.confirm('Sign out of all devices? You will need to log in again everywhere.')) {
      return;
    }
    try {
      await api.signOutAllSessions();
    } catch (e) {
      console.error('Sign out everywhere failed:', e);
      window.alert('Could not sign out of all devices. Please try again.');
      return;
    }
    onLogout();
  };

  // Auto-expand the selected category when it changes
  useEffect(() => {
    if (selectedCategory && selectedCategory !== 'all') {
      setExpandedCategories(prev => ({
        ...prev,
        [selectedCategory]: true,
      }));
    }
  }, [selectedCategory]);

  const toggleExpand = (categoryId, e) => {
    e.stopPropagation();
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const hasSubcategories = (category) => {
    return category.subcategories && category.subcategories.length > 0;
  };

  // Group chat sessions by date
  const groupSessionsByDate = (sessions) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups = { today: [], yesterday: [], earlier: [] };
    sessions.forEach(session => {
      // GET /chat/sessions calls this last_activity. Reading last_active gave
      // every session an Invalid Date, so they all fell through to "Earlier"
      // and the Today/Yesterday groups never rendered (COL-36). No producer
      // emits last_active or created_at, so there is nothing to fall back to.
      const date = new Date(session.last_activity);
      if (date >= today) {
        groups.today.push(session);
      } else if (date >= yesterday) {
        groups.yesterday.push(session);
      } else {
        groups.earlier.push(session);
      }
    });
    return groups;
  };

  // Get display name for session
  const getSessionDisplayName = (session) => {
    if (session.title) return session.title;
    // COL-73: the server is now the sole minter of session ids (a bare
    // uuid) — the client no longer mints its own `session-<uuid>` ids, so
    // there's no second format to normalize away here going forward. A
    // pre-existing session created before this change keeps its literal
    // `session-` prefix forever (there's no migration), so an untitled one
    // may still show it in this fallback label — a cosmetic-only difference
    // for old data, not a functional one (it still lists and opens fine).
    const id = session.session_id || '';
    return id ? `Chat ${id.substring(0, 12)}...` : 'Untitled chat';
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'var(--scrim)',
            zIndex: 99,
          }}
          onClick={onClose}
        />
      )}

      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-brand-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </div>
            <span className="sidebar-brand-text">Collections</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {chatConfig ? (
            /* Chat session list */
            <ChatSessionNav
              chatConfig={chatConfig}
              groupSessionsByDate={groupSessionsByDate}
              getSessionDisplayName={getSessionDisplayName}
            />
          ) : collectionsConfig ? (
            /* Collections navigation */
            <CollectionsNav collectionsConfig={collectionsConfig} />
          ) : (
            /* Category navigation */
            <CategoryNav
              categories={categories}
              selectedCategory={selectedCategory}
              selectedSubcategory={selectedSubcategory}
              onSelectCategory={onSelectCategory}
              expandedCategories={expandedCategories}
              toggleExpand={toggleExpand}
              hasSubcategories={hasSubcategories}
            />
          )}
        </nav>

        {/* Tools Section */}
        <div className="sidebar-tools">
          <div className="sidebar-section-title">Tools</div>
          <SidebarToolLink to="/chat" active={location.pathname.startsWith('/chat')} label="Chat">
            <ChatCircle size={16} />
          </SidebarToolLink>
          <SidebarToolLink to="/" active={location.pathname === '/'} label="Items">
            <SquaresFour size={16} />
          </SidebarToolLink>
          <SidebarToolLink to="/entities" active={location.pathname === '/entities'} label="Entities">
            <ShareNetwork size={16} />
          </SidebarToolLink>
          <SidebarToolLink to="/places" active={location.pathname === '/places'} label="Places">
            <MapPin size={16} />
          </SidebarToolLink>
          <SidebarToolLink to="/collections" active={location.pathname.startsWith('/collections')} label="My Collections">
            <FolderSimple size={16} />
          </SidebarToolLink>
          <SidebarToolLink to="/settings" active={location.pathname === '/settings'} label="Settings">
            <Gear size={16} />
          </SidebarToolLink>
          {user?.isAdmin && (
            <SidebarToolLink to="/review" active={location.pathname === '/review'} label="Review Tool">
              <NotePencil size={16} />
            </SidebarToolLink>
          )}
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-user-avatar">
              {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="sidebar-user-identity">
              <div className="sidebar-user-name">
                {user?.name || user?.email?.split('@')[0]}
              </div>
              <div className="sidebar-user-email">
                {user?.email}
              </div>
            </div>
          </div>

          <div className="sidebar-footer-actions">
            <button onClick={onLogout} className="sidebar-signout-btn">
              <SignOut size={16} />
              Sign out
            </button>
            <button onClick={handleSignOutEverywhere} className="sidebar-signout-btn" title="Sign out of all devices">
              <ShieldWarning size={16} />
              Sign out everywhere
            </button>
            <ThemeToggle />
          </div>
        </div>
      </aside>
    </>
  );
}

/** Category navigation content for Gallery/Entities pages */
function CategoryNav({ categories, selectedCategory, selectedSubcategory, onSelectCategory, expandedCategories, toggleExpand, hasSubcategories }) {
  return (
    <>
      <div className="sidebar-section-title">
        {categories.filter(c => c.id !== 'all').length} categories available
      </div>

      <ul className="category-list">
        {categories.map((category, index) => (
          <motion.li
            key={category.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.02 }}
          >
            <div
              className={`category-item ${selectedCategory === category.id && !selectedSubcategory ? 'active' : ''}`}
              onClick={() => onSelectCategory(category.id, null)}
            >
              <span className="category-item-name">
                {hasSubcategories(category) ? (
                  <span
                    className={`category-expand-icon ${expandedCategories[category.id] ? 'expanded' : ''}`}
                    onClick={(e) => toggleExpand(category.id, e)}
                  >
                    <CaretRight size={12} />
                  </span>
                ) : (
                  <span style={{ width: 24, display: 'inline-block' }} />
                )}
                {category.name}
              </span>
              <span className="category-item-count">{category.count}</span>
            </div>

            {/* Subcategories */}
            <AnimatePresence>
              {hasSubcategories(category) && expandedCategories[category.id] && (
                <motion.ul
                  className="subcategory-list"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ overflow: 'hidden', paddingLeft: 20 }}
                >
                  {category.subcategories.map((sub) => (
                    <li key={sub.id}>
                      <div
                        className={[
                          'category-item subcategory-item',
                          selectedCategory === category.id && selectedSubcategory === sub.id ? 'active' : '',
                          sub.disabled ? 'disabled' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!sub.disabled) {
                            onSelectCategory(category.id, sub.id);
                          }
                        }}
                        style={sub.disabled ? { opacity: 0.5, cursor: 'default' } : undefined}
                      >
                        <span className="category-item-name">{sub.name}</span>
                        <span className="category-item-count">{sub.count}</span>
                      </div>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </motion.li>
        ))}
      </ul>
    </>
  );
}

/** Chat session navigation content for Chat page */
function ChatSessionNav({ chatConfig, groupSessionsByDate, getSessionDisplayName }) {
  const { sessions, activeSessionId, isLoading, onSelect, onNew, onDelete } = chatConfig;

  const groups = groupSessionsByDate(sessions);

  return (
    <div className="sidebar-chat-nav">
      <button
        className="sidebar-new-chat-btn"
        onClick={onNew}
      >
        <PlusIcon />
        <span>New conversation</span>
      </button>

      {isLoading ? (
        <div className="sidebar-chat-loading">
          <div className="chat-bounce-dot" />
          <div className="chat-bounce-dot" />
          <div className="chat-bounce-dot" />
        </div>
      ) : sessions.length === 0 ? (
        <div className="sidebar-chat-empty">
          No previous conversations
        </div>
      ) : (
        <div className="sidebar-session-list">
          {groups.today.length > 0 && (
            <SessionGroup
              label="Today"
              sessions={groups.today}
              activeSessionId={activeSessionId}
              onSelect={onSelect}
              onDelete={onDelete}
              getDisplayName={getSessionDisplayName}
            />
          )}
          {groups.yesterday.length > 0 && (
            <SessionGroup
              label="Yesterday"
              sessions={groups.yesterday}
              activeSessionId={activeSessionId}
              onSelect={onSelect}
              onDelete={onDelete}
              getDisplayName={getSessionDisplayName}
            />
          )}
          {groups.earlier.length > 0 && (
            <SessionGroup
              label="Earlier"
              sessions={groups.earlier}
              activeSessionId={activeSessionId}
              onSelect={onSelect}
              onDelete={onDelete}
              getDisplayName={getSessionDisplayName}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SessionGroup({ label, sessions, activeSessionId, onSelect, onDelete, getDisplayName }) {
  return (
    <div className="sidebar-session-group">
      <div className="sidebar-session-group-label">{label}</div>
      {sessions.map(session => (
        <div
          key={session.session_id}
          role="button"
          tabIndex={0}
          className={`sidebar-session-item ${activeSessionId === session.session_id ? 'active' : ''}`}
          onClick={() => onSelect(session.session_id)}
          onKeyDown={(e) => e.key === 'Enter' && onSelect(session.session_id)}
        >
          <ChatBubbleIcon />
          <span className="sidebar-session-name">
            {getDisplayName(session)}
          </span>
          <button
            className="sidebar-session-delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(session.session_id, e);
            }}
            title="Delete conversation"
            aria-label="Delete conversation"
          >
            <TrashIcon />
          </button>
        </div>
      ))}
    </div>
  );
}

/** Collections navigation content for My Collections / Collection Detail pages */
function CollectionsNav({ collectionsConfig }) {
  const { collections = [], activeCollectionId } = collectionsConfig;

  return (
    <>
      <div className="sidebar-section-title">
        {collections.length} {collections.length === 1 ? 'collection' : 'collections'}
      </div>

      <ul className="category-list">
        {collections.map((collection, index) => (
          <motion.li
            key={collection.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.02 }}
          >
            <Link
              to={`/collections/${collection.id}`}
              className={`category-item ${activeCollectionId === collection.id ? 'active' : ''}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <span className="category-item-name">
                <span style={{ width: 24, display: 'inline-block' }} />
                {collection.name}
              </span>
              <span className="category-item-count">{collection.item_count ?? 0}</span>
            </Link>
          </motion.li>
        ))}
      </ul>
    </>
  );
}

function SidebarToolLink({ to, active, label, children }) {
  return (
    <Link
      to={to}
      className={`sidebar-tool-link ${active ? 'active' : ''}`}
    >
      {children}
      {label}
    </Link>
  );
}

// Icons
function PlusIcon() {
  return <Plus size={16} />;
}

function ChatBubbleIcon() {
  return <ChatCircle size={14} />;
}

function TrashIcon() {
  return <Trash size={14} />;
}
